import { semantic, system } from "@/constants/colors";
import { fonts } from "@/constants/fonts";
import { ToastType, useToastStore } from "@/store/ToastStore";
import { Ionicons } from "@expo/vector-icons";
import { BlurView } from "expo-blur";
import * as Haptics from "expo-haptics";
import React, { useEffect } from "react";
import { Dimensions, StyleSheet, useColorScheme, View } from "react-native";
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import ThemedText from "../core/ThemedText";

const { width } = Dimensions.get("window");
const SPRING_CONFIG = {
  damping: 15,
  mass: 1,
  stiffness: 120,
};

const Toast: React.FC = () => {
  const { message, type, isVisible, hideToast, duration } = useToastStore();
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";

  const translateY = useSharedValue(-100);
  const opacity = useSharedValue(0);

  useEffect(() => {
    if (isVisible) {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);

      translateY.value = withSpring(50, SPRING_CONFIG);
      opacity.value = withSpring(1, SPRING_CONFIG);

      const hideTimer = setTimeout(() => {
        translateY.value = withSpring(-100, SPRING_CONFIG);
        opacity.value = withTiming(0, { duration: 200 }, (finished) => {
          if (finished) {
            runOnJS(hideToast)();
          }
        });
      }, duration);

      return () => clearTimeout(hideTimer);
    }
  }, [isVisible, duration, translateY, opacity, hideToast]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
    opacity: opacity.value,
  }));

  const getIconProps = (
    toastType: ToastType
  ): { name: keyof typeof Ionicons.glyphMap; color: string } => {
    switch (toastType) {
      case "success":
        return { name: "checkmark-circle-outline", color: system.green };
      case "error":
        return { name: "close-circle-outline", color: system.red };
      case "warning":
        return { name: "warning-outline", color: semantic.warning.main };
      case "info":
      default:
        return {
          name: "information-circle-outline",
          color: semantic.info.main,
        };
    }
  };

  if (!isVisible) return null;

  const { name, color } = getIconProps(type);

  return (
    <Animated.View style={[styles.container, animatedStyle]}>
      <BlurView
        intensity={isDark ? 40 : 60}
        tint={isDark ? "dark" : "light"}
        style={styles.blurContainer}
      >
        <View style={styles.content}>
          <Ionicons name={name} size={20} color={color} style={styles.icon} />
          <ThemedText style={styles.message}>{message}</ThemedText>
        </View>
      </BlurView>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    alignSelf: "center",
    top: 0,
    width: Math.min(width - 32, 400),
    zIndex: 1000,
  },
  blurContainer: {
    borderRadius: 12,
    overflow: "hidden",
    padding: 12,
    backgroundColor: "#292929",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  content: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    paddingHorizontal: 4,
  },
  icon: {
    marginRight: 8,
  },
  message: {
    fontSize: 14,
    color: "white",
    fontFamily: fonts.medium,
  },
});

export default Toast;
